package com.trevorwiebe.timesheet.settlements.presentation

import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.domain.formatPercent
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import com.trevorwiebe.timesheet.invoicing.domain.isBillable
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.settlements.domain.CARRIER_BASE_PAY_PERCENT
import com.trevorwiebe.timesheet.settlements.domain.DeductionTotal
import com.trevorwiebe.timesheet.settlements.domain.carrierSettleableLoads
import com.trevorwiebe.timesheet.settlements.domain.deductionsYearToDate
import com.trevorwiebe.timesheet.settlements.domain.driversOf
import com.trevorwiebe.timesheet.settlements.domain.settleableLoads
import com.trevorwiebe.timesheet.settlements.domain.settledLoadIds
import com.trevorwiebe.timesheet.settlements.domain.settlementName
import com.trevorwiebe.timesheet.settlements.domain.toSettlementLine
import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementCharge
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementLine
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementStatus
import kotlinx.datetime.LocalDate

enum class SettlementListTab(val label: String) { DRAFTS("Drafts"), SENT("Sent") }

/** A load offered on the new-settlement screen, with the line it would become. */
data class SettleableLoad(
    val load: Load,
    val line: SettlementLine,
    // A load with no rate would pay nothing, so it has to be priced first.
    val isPayable: Boolean,
)

data class NewSettlementState(
    // One of these is set: who the settlement pays.
    val ownerOperatorId: String = "",
    val carrierId: String = "",
    // Null means every outstanding load, however old.
    val startDate: LocalDate? = null,
    val endDate: LocalDate,
    val selectedLoadIds: Set<String> = emptySet(),
    val showStartPicker: Boolean = false,
    val showEndPicker: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
)

data class ChargePercentInput(val charge: SettlementCharge, val amount: Int, val percentText: String) {
    val amountFormatted: String get() = "$" + amount.formatCentsAsDollars()
}

/** Editing the pay percentages of one load on a draft: its base rate and each extra charge. */
data class LinePayDialogState(
    val loadId: String,
    val baseText: String,
    val charges: List<ChargePercentInput>,
    val isSaving: Boolean = false,
    val error: String? = null,
)

/** Adding ([index] null) or editing a deduction on a draft. */
data class DeductionDialogState(
    val index: Int? = null,
    val description: String = "",
    val amountText: String = "",
    val isSaving: Boolean = false,
    val error: String? = null,
)

data class SendSettlementState(
    val to: String,
    val isSending: Boolean = false,
    val error: String? = null,
)

/** A driver's loads on a settlement, for showing them grouped. */
data class DriverLines(val driverName: String, val lines: List<SettlementLine>) {
    val payFormatted: String get() = "$" + lines.sumOf { it.pay }.formatCentsAsDollars()
}

data class SettlementsState(
    val today: LocalDate,
    val settlements: List<Settlement> = emptyList(),
    val ownerOperators: List<OwnerOperator> = emptyList(),
    val carriers: List<Carrier> = emptyList(),
    val users: List<DatabaseUserModel> = emptyList(),
    val loads: List<Load> = emptyList(),
    val locations: List<Location> = emptyList(),
    val isLoading: Boolean = true,
    val error: String? = null,
    val tab: SettlementListTab = SettlementListTab.DRAFTS,
    val newSettlement: NewSettlementState? = null,
    val selectedSettlementId: String? = null,
    val showDeleteConfirm: Boolean = false,
    val isDeleting: Boolean = false,
    val showMarkSentConfirm: Boolean = false,
    val isMarkingSent: Boolean = false,
    val lineDialog: LinePayDialogState? = null,
    val deductionDialog: DeductionDialogState? = null,
    val isGeneratingPdf: Boolean = false,
    // Set when a PDF link is ready; the UI opens it and reports OnPdfOpened.
    val pdfUrlToOpen: String? = null,
    val sendDialog: SendSettlementState? = null,
    val infoTitle: String = "",
    val infoMessage: String? = null,
) {
    val visibleSettlements: List<Settlement>
        get() = settlements.filter {
            when (tab) {
                SettlementListTab.DRAFTS -> it.status == SettlementStatus.DRAFT
                SettlementListTab.SENT -> it.status == SettlementStatus.SENT
            }
        }

    val selectedSettlement: Settlement? get() = settlements.firstOrNull { it.id == selectedSettlementId }

    val activeOwnerOperators: List<OwnerOperator>
        get() = ownerOperators.filter { it.active && !it.deleted }.sortedBy { it.settlementName().lowercase() }

    fun ownerOperatorName(ownerOperator: OwnerOperator): String = ownerOperator.settlementName()

    val activeCarriers: List<Carrier>
        get() = carriers.filter { it.active && !it.deleted }.sortedBy { it.settlementName().lowercase() }

    fun carrierName(carrier: Carrier): String = carrier.settlementName()

    fun driverName(driverId: String): String =
        users.firstOrNull { it.id == driverId }?.name?.ifBlank { null } ?: "Unknown driver"

    /** Unsettled, payable loads per owner operator, for the counts beside each one. */
    val outstandingByOwnerOperator: Map<String, Int>
        get() {
            val settled = settledLoadIds(settlements)
            return activeOwnerOperators.associate { ownerOperator ->
                val drivers = driversOf(users, ownerOperator.id).mapTo(mutableSetOf()) { it.id }
                ownerOperator.id to settleableLoads(loads, settled, drivers, null, today, today).count { it.isBillable }
            }
        }

    /** Unsettled, payable loads per carrier, for the counts beside each one. */
    val outstandingByCarrier: Map<String, Int>
        get() {
            val settled = settledLoadIds(settlements)
            return activeCarriers.associate { carrier ->
                carrier.id to carrierSettleableLoads(loads, settled, carrier.id, null, today, today).count { it.isBillable }
            }
        }

    val newSettlementCarrier: Carrier?
        get() = carriers.firstOrNull { it.id == newSettlement?.carrierId }

    /** Whoever the new settlement pays, for the picker. */
    val newSettlementPayeeName: String?
        get() = newSettlementOwnerOperator?.settlementName() ?: newSettlementCarrier?.settlementName()

    val newSettlementOwnerOperator: OwnerOperator?
        get() = ownerOperators.firstOrNull { it.id == newSettlement?.ownerOperatorId }

    val newSettlementDrivers: List<DatabaseUserModel>
        get() = newSettlementOwnerOperator?.let { driversOf(users, it.id) } ?: emptyList()

    val settleableLoads: List<SettleableLoad>
        get() {
            val form = newSettlement ?: return emptyList()
            newSettlementCarrier?.let { carrier ->
                val name = carrier.settlementName()
                return carrierSettleableLoads(loads, settledLoadIds(settlements), carrier.id, form.startDate, form.endDate, today)
                    .map { SettleableLoad(it, it.toSettlementLine(locations, name, CARRIER_BASE_PAY_PERCENT), it.isBillable) }
            }
            val ownerOperator = newSettlementOwnerOperator ?: return emptyList()
            val driverIds = newSettlementDrivers.mapTo(mutableSetOf()) { it.id }
            val basePay = ownerOperator.basePayPercent ?: 0.0
            return settleableLoads(loads, settledLoadIds(settlements), driverIds, form.startDate, form.endDate, today)
                .map { SettleableLoad(it, it.toSettlementLine(locations, driverName(it.employeeId), basePay), it.isBillable) }
        }

    private val selectedNewLines: List<SettlementLine>
        get() {
            val selected = newSettlement?.selectedLoadIds ?: return emptyList()
            return settleableLoads.filter { it.load.id in selected && it.isPayable }.map { it.line }
        }

    val newSettlementSummary: String
        get() {
            val lines = selectedNewLines
            val gross = "$" + lines.sumOf { it.gross }.formatCentsAsDollars()
            val pay = "$" + lines.sumOf { it.pay }.formatCentsAsDollars()
            return "${lines.size} of ${settleableLoads.size} loads • $gross gross • $pay pay"
        }

    /** Why a settlement can't be made for the chosen owner operator, before any loads are listed. */
    val newSettlementBlocker: String?
        get() {
            val ownerOperator = newSettlementOwnerOperator ?: return null
            val name = ownerOperator.settlementName()
            return when {
                ownerOperator.basePayPercent == null ->
                    "Set $name's base pay % in Admin > Owner Operators before settling."
                newSettlementDrivers.isEmpty() ->
                    "No drivers are assigned to $name. Choose their owner operator in Admin > User Management."
                else -> null
            }
        }

    val newSettlementBasePayLabel: String?
        get() {
            val percent = if (newSettlementCarrier != null) CARRIER_BASE_PAY_PERCENT else newSettlementOwnerOperator?.basePayPercent
            return percent?.let { "${it.formatPercent()}% of base rate • extra charges at 100% • change per load on the draft" }
        }

    val unpayableNotice: String?
        get() {
            val count = settleableLoads.count { !it.isPayable }
            if (count == 0) return null
            return if (count == 1) "1 load needs a rate before it can be settled. Tap a load to add its rate."
            else "$count loads need a rate before they can be settled. Tap a load to add its rate."
        }

    val canCreateSettlement: Boolean
        get() = newSettlement?.let { !it.isSaving && selectedNewLines.isNotEmpty() } == true &&
            newSettlementBlocker == null

    // ── Detail ────────────────────────────────────────────────────────────

    val selectedLinesByDriver: List<DriverLines>
        get() = selectedSettlement?.lines.orEmpty()
            .groupBy { it.driverName }
            .map { (driver, lines) -> DriverLines(driver, lines.sortedBy { it.date }) }
            .sortedBy { it.driverName.lowercase() }

    val selectedDeductionTotals: List<DeductionTotal>
        get() = selectedSettlement?.let { deductionsYearToDate(it, settlements) } ?: emptyList()

    /** Deduction names used before for this owner operator, so repeat entries match for year to date. */
    val deductionSuggestions: List<String>
        get() {
            val ownerOperatorId = selectedSettlement?.ownerOperatorId ?: return emptyList()
            val typed = deductionDialog?.description?.trim()?.lowercase().orEmpty()
            return settlements
                .filter { it.ownerOperatorId == ownerOperatorId }
                .flatMap { it.deductions.map { d -> d.description.trim() } }
                .distinctBy { it.lowercase() }
                .filter { it.isNotEmpty() && it.lowercase() != typed && it.lowercase().contains(typed) }
                .sorted()
        }

    val lineDialogLine: SettlementLine?
        get() = selectedSettlement?.lines?.firstOrNull { it.loadId == lineDialog?.loadId }
}
